import React, { useState, useEffect, useRef } from 'react';

const FHO5_QUESTIONS = [
  { id: 'freedom', text: "I feel free to be my authentic self", emoji: '🦅', low: 'Dependency', high: 'Freedom' },
  { id: 'happiness', text: "I experience genuine happiness from within", emoji: '☀️', low: 'Despair', high: 'Happiness' },
  { id: 'oneness', text: "I feel connected and in harmony with others", emoji: '🤝', low: 'Discord', high: 'Oneness' },
  { id: 'bfa', text: "My beliefs empower me to take positive action", emoji: '💪', low: 'Stuck', high: 'Empowered' },
  { id: 'mindstream', text: "I feel Power, Flow, and Clarity in my life", emoji: '✨', low: 'Blocked', high: 'Flowing' }
];

const RESPONSE_OPTIONS = [
  { value: 1, label: 'Never', emoji: '😔', state: 'Deep in 3D Swamp' },
  { value: 2, label: 'Rarely', emoji: '😕', state: 'Struggling' },
  { value: 3, label: 'So-So', emoji: '😐', state: 'So-Soville' },
  { value: 4, label: 'Often', emoji: '🙂', state: 'Growing' },
  { value: 5, label: 'Always', emoji: '😄', state: 'Free Happy One' }
];

const getScoreInterpretation = (score) => {
  if (score <= 9) return { 
    state: 'Deep in 3D Swamp', 
    emoji: '🌑', 
    message: "This reading sits in the band the framework calls Deep in the 3D Swamp. It is a starting point, not a verdict, and you do not start alone. Noticing where you are is the first step of the climb.",
    color: '#ef4444'
  };
  if (score <= 14) return { 
    state: 'Wearing the Hood', 
    emoji: '😔', 
    message: "This reading sits in the band the framework calls Wearing the Hood. It is a starting point, not a verdict. The Light is within you, and the work from here is loosening the beliefs, feelings and actions that have been limiting you.",
    color: '#f97316'
  };
  if (score === 15) return { 
    state: 'So-Soville', 
    emoji: '😐', 
    message: "You're in So-Soville - a place of settled mediocrity. You have the potential for so much more! It's time to choose Freedom, Happiness, and Oneness.",
    color: '#eab308'
  };
  if (score <= 20) return { 
    state: 'Climbing Out', 
    emoji: '🌱', 
    message: "You're climbing out of the 3D Swamp! Keep using Autocebos - Powerful Beliefs, Positive Feelings, and Productive Actions - to continue your ascension.",
    color: '#22c55e'
  };
  return { 
    state: 'Free Happy One', 
    emoji: '🌟', 
    message: "You're living as a Free Happy One! Your Power, Flow, and Clarity are strong. Keep Understanding, Embracing, and Demonstrating your divine potential.",
    color: '#10b981'
  };
};

const WellbeingCheckin = ({ isOpen, onClose, onComplete, authToken, context, palette }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [result, setResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasSubmitted = useRef(false);

  useEffect(() => {
    if (isOpen && !hasSubmitted.current) {
      setCurrentStep(0);
      setResponses({});
      setResult(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const submitAssessment = async (finalResponses) => {
    if (hasSubmitted.current) return;
    hasSubmitted.current = true;
    setIsSubmitting(true);
    
    // Calculate FHO-5 score (5-25 scale, multiply by 4 for 0-100)
    const rawScore = Object.values(finalResponses).reduce((a, b) => a + b, 0);
    const percentScore = Math.round((rawScore / 25) * 100);
    const interpretation = getScoreInterpretation(rawScore);
    
    try {
      const response = await fetch('/api/v1/journey/assessment?assessment_type=fho5' + (context ? '&context=' + encodeURIComponent(context) : ''), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authToken
        },
        body: JSON.stringify({ ...finalResponses, raw_score: rawScore, percent_score: percentScore })
      });
      const data = await response.json();
      setResult({
        ...data,
        score: percentScore,
        rawScore: rawScore,
        interpretation: interpretation
      });
      setCurrentStep(6);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Failed to submit:', error);
      hasSubmitted.current = false;
      setIsSubmitting(false);
    }
  };

  const handleResponse = (questionId, value) => {
    const newResponses = { ...responses, [questionId]: value };
    setResponses(newResponses);

    // Submit on the final question, advance otherwise — keyed off step, not key count
    const isLastQuestion = currentStep >= FHO5_QUESTIONS.length;

    setTimeout(() => {
      if (isLastQuestion) {
        if (!hasSubmitted.current) {
          submitAssessment(newResponses);
        }
      } else {
        setCurrentStep(prev => prev + 1);
      }
    }, 300);
  };

  const handleClose = () => {
    hasSubmitted.current = false;
    if (result && onComplete) {
      onComplete(result);
    }
    onClose();
  };

  if (!isOpen) return null;

  const styles = paletteStyles(palette);
  const currentQuestion = currentStep >= 1 && currentStep <= 5 ? FHO5_QUESTIONS[currentStep - 1] : null;
  const progress = Math.min(currentStep, 6) / 6 * 100;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.progressBar}>
          <div style={{ ...styles.progressFill, width: progress + '%' }} />
        </div>

        {currentStep === 0 && !isSubmitting && !result && (
          <div style={styles.content}>
            <div style={styles.iconLarge}>
              <span style={{color: '#667eea'}}>F</span>
              <span style={{color: '#ef4444'}}>H</span>
              <span style={{color: '#10b981'}}>O</span>
            </div>
            <h2 style={styles.title}>FHO-5 Check-In</h2>
            <p style={styles.subtitle}>
              Let's measure your Freedom, Happiness, and Oneness to see where you are on your transformation journey.
            </p>
            <p style={styles.hint}>
              This helps track your ascension from the 3D Swamp toward becoming a Free Happy One. ✨
            </p>
            <p style={styles.timeEstimate}>Takes about 1 minute</p>
            <button style={styles.primaryButton} onClick={() => setCurrentStep(1)}>
              Begin FHO-5 Check-In
            </button>
            <button style={styles.skipButton} onClick={handleClose}>
              Maybe later
            </button>
          </div>
        )}

        {currentStep >= 1 && currentStep <= 5 && currentQuestion && !isSubmitting && !result && (
          <div style={styles.content}>
            <div style={styles.questionNumber}>
              {currentStep} of {FHO5_QUESTIONS.length}
            </div>
            <div style={styles.questionEmoji}>{currentQuestion.emoji}</div>
            <h3 style={styles.questionText}>{context ? 'Over the past 7 days...' : 'Over the past 2 weeks...'}</h3>
            <p style={styles.statement}>{currentQuestion.text}</p>
            <div style={styles.scaleHint}>
              <span style={{color: '#ef4444'}}>{currentQuestion.low}</span>
              <span style={{color: '#eab308'}}>So-So</span>
              <span style={{color: '#10b981'}}>{currentQuestion.high}</span>
            </div>
            
            <div style={styles.optionsGrid}>
              {RESPONSE_OPTIONS.map(option => (
                <button
                  key={option.value}
                  style={{
                    ...styles.optionButton,
                    ...(responses[currentQuestion.id] === option.value ? styles.optionSelected : {}),
                    ...(option.value === 3 ? styles.sosoOption : {})
                  }}
                  onClick={() => handleResponse(currentQuestion.id, option.value)}
                >
                  <span style={styles.optionEmoji}>{option.emoji}</span>
                  <span style={styles.optionLabel}>{option.label}</span>
                </button>
              ))}
            </div>

            {currentStep > 1 && (
              <button style={styles.backButton} onClick={() => setCurrentStep(currentStep - 1)}>
                ← Back
              </button>
            )}
          </div>
        )}

        {isSubmitting && (
          <div style={styles.content}>
            <div style={styles.iconLarge}>⏳</div>
            <h2 style={styles.title}>Calculating your FHO State...</h2>
            <p style={styles.subtitle}>Analyzing your Power, Flow, and Clarity</p>
          </div>
        )}

        {result && !isSubmitting && (
          <div style={styles.content}>
            <div style={styles.iconLarge}>{result.interpretation?.emoji || '🌟'}</div>
            <h2 style={{...styles.title, color: result.interpretation?.color}}>
              {result.interpretation?.state || 'Check-in Complete'}
            </h2>
            <p style={styles.resultMessage}>{result.interpretation?.message || result.message}</p>
            
            {result.improvement && result.improvement > 0 && (
              <div style={styles.improvementBadge}>
                📈 +{result.improvement}% since last time!
              </div>
            )}
            
            <div style={styles.scoreDisplay}>
              <span style={styles.scoreLabel}>FHO Transformation Score</span>
              <span style={{...styles.scoreValue, color: result.interpretation?.color}}>
                {result.rawScore}/25
              </span>
              <div style={styles.scoreBar}>
                <div style={{
                  ...styles.scoreBarFill,
                  width: `${(result.rawScore / 25) * 100}%`,
                  backgroundColor: result.interpretation?.color
                }} />
                <div style={styles.sosoMarker} title="So-Soville (15)">
                </div>
              </div>
              <div style={styles.scoreScale}>
                <span>3D Swamp</span>
                <span>So-Soville</span>
                <span>Free Happy One</span>
              </div>
            </div>
            
            <button style={styles.primaryButton} onClick={handleClose}>
              Continue Your Journey
            </button>
          </div>
        )}

        <button style={styles.closeButton} onClick={handleClose}>×</button>
      </div>
    </div>
  );
};

const BASE_STYLES = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  },
  modal: {
    backgroundColor: '#1a1a2e',
    borderRadius: '20px',
    maxWidth: '480px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    position: 'relative',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)'
  },
  progressBar: {
    height: '4px',
    backgroundColor: '#2a2a4e',
    borderRadius: '20px 20px 0 0',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #667eea, #ef4444, #10b981)',
    transition: 'width 0.3s ease'
  },
  content: {
    padding: '40px 30px',
    textAlign: 'center'
  },
  iconLarge: {
    fontSize: '60px',
    marginBottom: '20px'
  },
  title: {
    color: '#ffffff',
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '12px'
  },
  subtitle: {
    color: '#a0a0c0',
    fontSize: '16px',
    lineHeight: '1.6',
    marginBottom: '16px'
  },
  hint: {
    color: '#8080a0',
    fontSize: '14px',
    marginBottom: '24px'
  },
  timeEstimate: {
    color: '#6b6b8a',
    fontSize: '13px',
    marginBottom: '24px'
  },
  questionNumber: {
    color: '#7c3aed',
    fontSize: '14px',
    fontWeight: '500',
    marginBottom: '16px'
  },
  questionEmoji: {
    fontSize: '48px',
    marginBottom: '20px'
  },
  questionText: {
    color: '#a0a0c0',
    fontSize: '14px',
    marginBottom: '8px'
  },
  statement: {
    color: '#ffffff',
    fontSize: '20px',
    fontWeight: '500',
    marginBottom: '16px',
    lineHeight: '1.4'
  },
  scaleHint: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    marginBottom: '20px',
    padding: '0 10px'
  },
  optionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '8px',
    marginBottom: '20px'
  },
  optionButton: {
    backgroundColor: '#2a2a4e',
    border: '2px solid transparent',
    borderRadius: '12px',
    padding: '14px 6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px'
  },
  optionSelected: {
    backgroundColor: '#7c3aed33',
    borderColor: '#7c3aed'
  },
  sosoOption: {
    borderColor: 'rgba(234, 179, 8, 0.3)'
  },
  optionEmoji: {
    fontSize: '24px'
  },
  optionLabel: {
    color: '#ffffff',
    fontSize: '11px'
  },
  primaryButton: {
    background: 'linear-gradient(135deg, #667eea, #10b981)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '12px',
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    marginTop: '16px'
  },
  skipButton: {
    backgroundColor: 'transparent',
    color: '#6b6b8a',
    border: 'none',
    padding: '12px',
    fontSize: '14px',
    cursor: 'pointer',
    marginTop: '12px'
  },
  backButton: {
    backgroundColor: 'transparent',
    color: '#7c3aed',
    border: 'none',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
    marginTop: '16px'
  },
  closeButton: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#6b6b8a',
    fontSize: '28px',
    cursor: 'pointer',
    padding: '4px 12px',
    lineHeight: '1'
  },
  resultMessage: {
    color: '#a0a0c0',
    fontSize: '16px',
    lineHeight: '1.6',
    marginBottom: '24px'
  },
  improvementBadge: {
    backgroundColor: '#10b98133',
    color: '#10b981',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '500',
    display: 'inline-block',
    marginBottom: '24px'
  },
  scoreDisplay: {
    backgroundColor: '#2a2a4e',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px'
  },
  scoreLabel: {
    color: '#a0a0c0',
    fontSize: '14px',
    display: 'block',
    marginBottom: '8px'
  },
  scoreValue: {
    fontSize: '36px',
    fontWeight: '700'
  },
  scoreBar: {
    height: '8px',
    backgroundColor: '#1a1a2e',
    borderRadius: '4px',
    marginTop: '16px',
    position: 'relative'
  },
  scoreBarFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.5s ease'
  },
  sosoMarker: {
    position: 'absolute',
    left: '60%',
    top: '-4px',
    width: '2px',
    height: '16px',
    backgroundColor: '#eab308'
  },
  sosoMarkerLabel: {
    position: 'absolute',
    top: '-20px',
    left: '-12px',
    fontSize: '10px',
    color: '#eab308'
  },
  scoreScale: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '10px',
    color: '#6b6b8a',
    marginTop: '8px'
  }
};

// In a pilot the form takes the coach's color scheme. Everywhere else it keeps the colors above.
// The F-H-O letters, the low / So-So / high hints and the result colors carry meaning and stay.
const paletteStyles = (p) => {
  if (!p) return BASE_STYLES;
  const s = BASE_STYLES;
  return {
    ...s,
    modal: { ...s.modal, backgroundColor: p.sidebarBg, border: `1px solid ${p.border}` },
    progressBar: { ...s.progressBar, backgroundColor: p.cardBg },
    progressFill: { ...s.progressFill, background: p.accent },
    title: { ...s.title, color: p.textPrimary },
    subtitle: { ...s.subtitle, color: p.textSecondary },
    hint: { ...s.hint, color: p.textSecondary },
    timeEstimate: { ...s.timeEstimate, color: p.textSecondary },
    questionNumber: { ...s.questionNumber, color: p.accent },
    questionText: { ...s.questionText, color: p.textSecondary },
    statement: { ...s.statement, color: p.textPrimary },
    optionButton: { ...s.optionButton, backgroundColor: p.cardBg },
    optionSelected: { backgroundColor: p.hoverBg, borderColor: p.accent },
    optionLabel: { ...s.optionLabel, color: p.textPrimary },
    primaryButton: { ...s.primaryButton, background: p.primaryGradient, color: p.textPrimary },
    skipButton: { ...s.skipButton, color: p.textSecondary },
    backButton: { ...s.backButton, color: p.accent },
    closeButton: { ...s.closeButton, color: p.textSecondary },
    resultMessage: { ...s.resultMessage, color: p.textSecondary },
    scoreDisplay: { ...s.scoreDisplay, backgroundColor: p.cardBg },
    scoreLabel: { ...s.scoreLabel, color: p.textSecondary },
    scoreBar: { ...s.scoreBar, backgroundColor: p.pageBg },
    scoreScale: { ...s.scoreScale, color: p.textSecondary },
  };
};

export default WellbeingCheckin;
